import { SaveFilled } from '@ant-design/icons'
import { useOrderMovements } from '@api/useOrderMovements'
import { NotePreview } from '@components/OrderWizard/Panes/DisplayPane/DisplayComponents/DriverNotePreview'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useModelConfig } from '@hooks/useModelConfig'
import api from '@utils/api'
import { Drawer, Form, Input, message, Popconfirm } from 'antd'
import moment, { Moment } from 'moment'
import React, { useState } from 'react'
import useMediaQuery from 'use-media-antd-query'

import OMPlusIcon from '../../../../../assets/CustomIcons/om_plus.svg'
import { MetaDetailsForm } from './MetaDetailsForm'
import { MovementForm } from './MovementForm'
import { useEasyOrderMovements } from './MovementStoreProvider'
import { DropSummaryCard } from './OrderSummaryCards/DropSummaryCard'
import { LoadSummaryCard } from './OrderSummaryCards/LoadSummaryCard'

const { TextArea } = Input

const getSiteIdFromUrl = () => {
  const paths = window.location.pathname.split('/')
  const siteId = paths[paths.length - 1]
  return siteId
}

const duplicateNumber = {
  category: 'duplicateNumber',
  msg: `Duplicate BOL Number Detected.  This BOL Number has been used within the last 48 hours. Are you sure you want to save?`,
}

export function EasyOrderMovementsDrawer() {
  const { isDrawerOpen, closeDrawer, activeOrder, form, isOrderReadOnly } = useEasyOrderMovements()
  const [isPopConfirmVisible, setIsPopConfirmVisible] = useState(false)
  const [title, setTitle] = useState(duplicateNumber)
  const { useSaveCompactOrderMovementsMutation } = useOrderMovements()
  const mutation = useSaveCompactOrderMovementsMutation(form, closeDrawer)
  const screenSize = useMediaQuery()
  const config = useModelConfig()

  const checkValues = () => {
    const movements = form.getFieldValue('movements')
    if (!movements) return
    const threshold = config?.config?.movement_gross_net_threshold || 0
    const hasOverThreshold = movements.some((m) => {
      const diff = Math.abs(m?.gross_quantity - m?.net_quantity)
      return diff > threshold
    })
    if (hasOverThreshold) {
      setIsPopConfirmVisible(true)
      setTitle({
        category: 'overThreshold',
        msg: `One or more movements have a difference between Gross and Net Volume greater than ${threshold}. Are you sure you want to save?`,
      })
      return
    }

    handleFinish()
  }

  const checkForDuplicate = async () => {
    const movements = form.getFieldValue('movements')
    if (!movements) return

    const localDuplicate = movements.filter((m, i, array) =>
      array.some((a, i2) => i !== i2 && a.bol_product === m.bol_product && a.number === m.number)
    )
    if (localDuplicate?.length) {
      setTitle({
        category: 'DuplicateBOL-Product',
        msg: `Please combine delivery information for BOL #: ${localDuplicate[0].number} and Product: ${localDuplicate[0].bol_product} into a single row.`,
      })
      setIsPopConfirmVisible(true)
      return
    }

    const remoteDupeResults = await Promise.all(
      movements?.map((m) =>
        api.checkDuplicateBolNumber({
          bol_number: m.number,
          bol_date: moment(m.date).toDate(),
        })
      )
    )

    const hasRemoteDupe = remoteDupeResults.some((r) => r.data === true)

    if (hasRemoteDupe) {
      setTitle(duplicateNumber)
      setIsPopConfirmVisible(true)
      return
    }

    checkValues()
  }

  const handleFinish = async () => {
    mutation.mutate({
      ...form.getFieldsValue(),
      site_id: getSiteIdFromUrl(),
      order_id: activeOrder?.id,
      order_number: activeOrder?.number,
    })
  }
  const handleValuesChange = (values) => {
    const currentMovements = form.getFieldValue('movements')
    const didBolNumberChange = values.movements?.some((m) => m.number)
    const didDateChange = values.movements?.some((m) => m.date)
    form.setFieldsValue({
      movements: currentMovements?.map((m) => ({
        ...m,
        number: didBolNumberChange ? values?.movements?.find((v) => v.number)?.number : m.number,
        date: didDateChange ? values?.movements?.find((v) => v.date)?.date : m.date,
      })),
    })
  }

  function handleErrors(params: any) {
    const firstError = params?.errorFields?.[0]?.errors?.[0]
    if (firstError) {
      message.error(firstError)
    }
  }

  const isPotatoMode = ['xs', 'sm', 'md', 'lg'].includes(screenSize)

  return (
    <Drawer
      title={`Order ${activeOrder?.number} Movements`}
      placement='bottom'
      visible={isDrawerOpen}
      onClose={closeDrawer}
      bodyStyle={{ padding: 0, margin: 0 }}
      height='90vh'
      size='large'
    >
      <Form
        form={form}
        onFinish={checkForDuplicate}
        onValuesChange={handleValuesChange}
        style={{ height: '100%' }}
        onFinishFailed={handleErrors}
      >
        <Vertical>
          <Horizontal
            className='bg-1 px-4 py-3 '
            alignItems={isPotatoMode ? 'flex-start' : 'center'}
            style={{ flexDirection: isPotatoMode ? 'column' : 'row', gap: 20 }}
          >
            <Horizontal justifyContent='center' height='auto' flex={1}>
              <img src={OMPlusIcon} className='ml-2 mr-5' style={{ maxHeight: 75, maxWidth: 75 }} />
              <Vertical verticalCenter className='pt-2'>
                <Texto category='h4'>Add Order Movements</Texto>
                <Texto>Fill out the compact form to generate your BOL's and Store Delivery Ticket information.</Texto>
                {activeOrder?.note?.content && <NotePreview className='mt-2' note={activeOrder?.note?.content} />}
              </Vertical>
            </Horizontal>

            <div style={{ display: 'flex' }}>
              <LoadSummaryCard components={activeOrder?.component_paths} selectedMarket={activeOrder?.market} />
              <DropSummaryCard components={activeOrder?.component_paths} selectedMarket={activeOrder?.market} />
            </div>
          </Horizontal>
          <Horizontal alignItems='center' justifyContent='flex-end' background='bg-2' className='py-2'>
            <MetaDetailsForm />
          </Horizontal>
          <Vertical className='bg-2' flex={3}>
            <MovementForm />
            <Horizontal className='px-4 py-3 bordered' justifyContent='space-between' alignItems='center'>
              <Texto category='h5'>
                {isOrderReadOnly && (
                  <>
                    <SaveFilled className='pr-2' />
                    Save Movement
                  </>
                )}
              </Texto>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <Form.Item name='attendant_notes' noStyle>
                  <TextArea
                    placeholder='Attendant Notes'
                    style={{ resize: 'none', height: 'auto', width: 600 }}
                    rows={2}
                    size='large'
                    maxLength={300}
                    showCount
                    disabled={isOrderReadOnly}
                    value={activeOrder?.attendant_notes}
                  />
                </Form.Item>
                <div>
                  {!isOrderReadOnly && (
                    <Popconfirm
                      title={title?.msg}
                      visible={isPopConfirmVisible}
                      onConfirm={() => {
                        if (title?.category === 'duplicateNumber') {
                          setIsPopConfirmVisible(false)
                          checkValues()
                        } else if (title?.category === 'overThreshold') {
                          setIsPopConfirmVisible(false)
                          handleFinish()
                        } else {
                          setIsPopConfirmVisible(false)
                        }
                      }}
                      onCancel={() => setIsPopConfirmVisible(false)}
                      okText={
                        title?.category === 'duplicateNumber' || title?.category === 'overThreshold' ? 'Yes' : 'OK'
                      }
                      cancelText={title?.category === 'overThreshold' ? 'Cancel' : 'No'}
                      showCancel={title?.category === 'duplicateNumber' || title?.category === 'overThreshold'}
                    >
                      <GraviButton
                        buttonText='Submit'
                        theme2
                        style={{ width: 120 }}
                        size='large'
                        onClick={() => form.submit()}
                        loading={mutation.isPending}
                      />
                    </Popconfirm>
                  )}
                </div>
              </div>
            </Horizontal>
          </Vertical>
        </Vertical>
      </Form>
    </Drawer>
  )
}
